/**
 * Charter quote PDF — compact professional quotation for the desk.
 *
 * Numbers come from Classic `quoteCharter` / trip P&L only. Grok does not
 * invent prices. Home currency is the company ledger unit. Northstar is the
 * software vendor; the operator (Celtic Air Services fiction, or the desk
 * company name) is on the letterhead. This is not a flight plan.
 */

import { hydrateCurrency, type CurrencyCode, type MoneyCatalogs } from "./catalogs";
import { formatHalifaxDateTime, halifaxParts } from "./clock";
import { homeCurrencyOf } from "./currency";
import { FLIGHT_TYPE_LABEL } from "./generator";
import { deskQuoteForRequest, type FetchInquiriesInput } from "./inbox";
import { isEmptyFerry, isPayingMxPositioning, isRevenueCharter, quoteCharter } from "./ledger";
import { quoteLogoImage, type AirlinePdfImage } from "./airlineBrand";
import { SOFTWARE_VENDOR, SUGGESTED_COMPANY_NAME } from "./profile";
import { buildTripPnl, ferriesForCharter, formatPnlBasis, type MoneyBasis } from "./tripPnl";
import type {
  AircraftSpec,
  CharterRequest,
  CompanyAccount,
  CompanyProfile,
  FlightLogEntry,
  Mission,
  MissionQuoteAttachment,
  RateTables,
  SceneryAirport,
} from "./types";

export const QUOTE_VENDOR = `${SOFTWARE_VENDOR} Dispatch`;
export const QUOTE_OPERATOR_FALLBACK = SUGGESTED_COMPANY_NAME;

const PAGE_W = 612;
const PAGE_H = 792;
const MARGIN = 48;
const RIGHT = PAGE_W - MARGIN;

export type QuoteMoneyLine = {
  label: string;
  amountCad: number;
  basis: MoneyBasis;
};

export type CharterQuoteDoc = {
  generatedAtUtc: string;
  filename: string;
  quoteRef: string;
  operatorName: string;
  homeIcao: string;
  vendorName: string;
  currency: CurrencyCode;
  clientName: string;
  clientCompany: string;
  trip: {
    stdLabel: string;
    originIcao: string;
    destIcao: string;
    originName: string;
    destName: string;
    aircraftName: string;
    registration: string;
    paxCount: number;
    cargoLb: number;
    flightTypeLabel: string;
  };
  quotedCad: number;
  lines: QuoteMoneyLine[];
  netCad: number;
  notes: string[];
  positioningNote: string | null;
  estimate: boolean;
  /** Pack or upload plate. Absent on older quotes and when no mark is set. */
  logoImage?: AirlinePdfImage | null;
};

export type BuildQuoteDocInput = {
  company: Pick<CompanyProfile, "operatorName" | "homeIcao"> & { opsDesk?: CompanyProfile["opsDesk"] };
  mission?: Mission;
  request?: CharterRequest;
  missions?: Mission[];
  rates: RateTables;
  catalogs?: MoneyCatalogs;
  account?: CompanyAccount;
  fleet?: AircraftSpec[];
  logs?: FlightLogEntry[];
  scenery?: SceneryAirport[];
  now?: Date;
  /** `brand:` pack ref or a small data URL. The plate draws an uploaded mark. */
  logoRef?: string | null;
  logoPlate?: string | null;
};

type PdfRun = { text: string; x: number; y: number; size: number; bold?: boolean };
type PdfRule = { x1: number; y1: number; x2: number; y2: number };

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function formatQuoteMoney(amount: number, currency: CurrencyCode): string {
  const abs = Math.abs(amount);
  const [whole, frac] = abs.toFixed(2).split(".");
  const grouped = (whole ?? "0").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const sign = amount < 0 ? "-" : "";
  return `${sign}${grouped}.${frac ?? "00"} ${hydrateCurrency(currency)}`;
}

export function operatorLetterhead(company?: Pick<CompanyProfile, "operatorName"> | null): string {
  const name = company?.operatorName?.trim();
  return name || QUOTE_OPERATOR_FALLBACK;
}

function ymdHalifax(date: Date): string {
  const p = halifaxParts(date);
  return `${p.year}${p.month}${p.day}`;
}

function sanitizeToken(raw: string, fallback = "TRIP"): string {
  const token = raw.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  return token || fallback;
}

export function quoteRefFor(input: {
  registration?: string;
  originIcao: string;
  destIcao: string;
  when: Date;
}): string {
  const tail = sanitizeToken(input.registration ?? "", "TAIL");
  return `Q-${tail}-${sanitizeToken(input.originIcao)}-${sanitizeToken(input.destIcao)}-${ymdHalifax(input.when)}`;
}

export function quoteFilename(ref: string): string {
  return `${ref.toLowerCase()}-quote.pdf`;
}

function uniqueNotes(notes: Array<string | null | undefined>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of notes) {
    const text = raw?.replace(/\s+/g, " ").trim();
    if (!text) continue;
    const key = text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(text);
  }
  return out;
}

function positioningNoteFor(input: {
  mission?: Mission;
  request?: CharterRequest;
  missions?: Mission[];
  deskNote?: string;
}): string | null {
  const row = input.mission ?? input.request;
  if (!row) return null;
  if (isPayingMxPositioning(row)) {
    return "Mx positioning charter — paying load on a shop positioning leg. Cheaper-than-usual desk rate card.";
  }
  if (isEmptyFerry(row)) {
    return "Company / empty ferry — positioning only. No charter revenue.";
  }
  const linked = input.mission ? ferriesForCharter(input.missions, input.mission) : [];
  if (linked.length) {
    const routes = linked.map((f) => `${f.originIcao}-${f.destIcao}`).join(", ");
    return `Positioning ferry ${routes} is empty / not revenue. Occupied sector is the quoted charter.`;
  }
  if (input.request?.itineraryKind === "tech_stop") {
    return "Tech / refuel stop is not a pax revenue sector. Offered fee is one charter plus empty-return unless a follow-on credits it.";
  }
  if (input.request?.returnEmpty) {
    return "Empty-return / reposition cost is already in the offered fee. A follow-on near dest reduces or waives that uplift.";
  }
  if (input.request?.itineraryKind === "backhaul") {
    return "Backhaul fill — discounted desk rate card. Paying charter, not a ferry.";
  }
  if (/position|ferry/i.test(input.deskNote ?? "")) return input.deskNote ?? null;
  return null;
}

function aircraftFor(mission: Mission | undefined, fleet?: ReadonlyArray<AircraftSpec>): AircraftSpec | undefined {
  if (!mission) return undefined;
  return fleet?.find((a) => a.id === mission.aircraftId || a.registration === mission.registration);
}

export function buildCharterQuoteDoc(input: BuildQuoteDocInput): CharterQuoteDoc | null {
  const now = input.now ?? new Date();
  const companyName = operatorLetterhead(input.company);
  const homeIcao = (input.company.homeIcao || "").toUpperCase();
  const currency = hydrateCurrency(
    input.account ? homeCurrencyOf(input.account) : (input.catalogs?.currency ?? "CAD"),
  );

  if (input.mission) {
    const mission = input.mission;
    const aircraft = aircraftFor(mission, input.fleet);
    const quote = quoteCharter(mission, input.rates, undefined, {
      catalogs: input.catalogs,
      aircraft,
      homeCurrency: currency,
      opsDesk: input.company.opsDesk,
    });
    const pnl = buildTripPnl({
      mission,
      missions: input.missions,
      logs: input.logs,
      account: input.account,
      rates: input.rates,
      catalogs: input.catalogs,
      fleet: input.fleet,
      currency,
      opsDesk: input.company.opsDesk,
    });
    const std = new Date(mission.departureIso);
    const when = Number.isNaN(std.getTime()) ? now : std;
    const quotedCad = isRevenueCharter(mission) ? quote.revenueCad : 0;
    const deskNote = input.request?.deskQuote?.note;
    const position = positioningNoteFor({
      mission,
      request: input.request,
      missions: input.missions,
      deskNote,
    });
    const lines: QuoteMoneyLine[] = [
      ...(input.request?.deskQuote?.baseCharterCad != null
        ? [
            { label: "Base charter", amountCad: input.request.deskQuote.baseCharterCad, basis: pnl.revenue.basis },
            ...(input.request.deskQuote.repositionCad
              ? [{ label: "Empty-return / reposition (in offered fee)", amountCad: input.request.deskQuote.repositionCad, basis: pnl.revenue.basis }]
              : []),
            ...(input.request.deskQuote.followOnCreditCad
              ? [{ label: "Follow-on credit (near dest)", amountCad: -input.request.deskQuote.followOnCreditCad, basis: pnl.revenue.basis }]
              : []),
          ]
        : []),
      { label: "Quoted charter", amountCad: quotedCad, basis: pnl.revenue.basis },
      { label: "Fuel", amountCad: pnl.fuel.amountCad, basis: pnl.fuel.basis },
      {
        label: "Landing / handling / parking",
        amountCad: Math.round((pnl.landing.amountCad + pnl.handling.amountCad + pnl.parking.amountCad) * 100) / 100,
        basis:
          pnl.landing.basis === "actual" && pnl.handling.basis === "actual" && pnl.parking.basis === "actual"
            ? "actual"
            : "estimate",
      },
    ];
    if (pnl.ferryCosts.amountCad) {
      lines.push({ label: "Positioning (non-revenue ferry)", amountCad: pnl.ferryCosts.amountCad, basis: pnl.ferryCosts.basis });
    }
    if (pnl.mxReserve.amountCad) {
      lines.push({ label: "Mx reserve (hours)", amountCad: pnl.mxReserve.amountCad, basis: pnl.mxReserve.basis });
    }
    if (pnl.engineReserve.amountCad) {
      lines.push({ label: "Engine reserve (hours)", amountCad: pnl.engineReserve.amountCad, basis: pnl.engineReserve.basis });
    }
    const estimate = lines.every((line) => line.basis === "estimate") || !pnl.posted;
    const ref = quoteRefFor({
      registration: mission.registration,
      originIcao: mission.originIcao,
      destIcao: mission.destIcao,
      when,
    });
    return {
      generatedAtUtc: now.toISOString(),
      filename: quoteFilename(ref),
      quoteRef: ref,
      operatorName: companyName,
      homeIcao,
      vendorName: QUOTE_VENDOR,
      currency,
      clientName: input.request?.clientName?.trim() ?? "",
      clientCompany: input.request?.clientCompany?.trim() ?? "",
      trip: {
        stdLabel: Number.isNaN(std.getTime()) ? "—" : formatHalifaxDateTime(std),
        originIcao: mission.originIcao,
        destIcao: mission.destIcao,
        originName: mission.originName,
        destName: mission.destName,
        aircraftName: mission.aircraftName,
        registration: mission.registration,
        paxCount: mission.paxCount,
        cargoLb: mission.cargoLb ?? 0,
        flightTypeLabel: FLIGHT_TYPE_LABEL[mission.flightType],
      },
      quotedCad,
      lines,
      netCad: pnl.netCad,
      notes: uniqueNotes([
        deskNote,
        mission.dispatchWarning,
        input.request?.specialRequests ? `Special: ${input.request.specialRequests}` : null,
        position,
        "Estimate from the company rate card and catalogs. Classic desk math — not invented figures.",
      ]),
      positioningNote: position,
      estimate,
      logoImage: quoteLogoImage({ logoRef: input.logoRef, logoPlate: input.logoPlate }),
    };
  }

  const request = input.request;
  if (!request || !input.rates) return null;
  const hintInput: FetchInquiriesInput = {
    scenery: input.scenery ?? [],
    fleet: input.fleet ?? [],
    rates: input.rates,
    catalogs: input.catalogs,
    opsDesk: input.company.opsDesk,
    now,
    homeIcao,
  };
  const desk = deskQuoteForRequest(request, hintInput) ?? request.deskQuote;
  if (!desk) return null;
  const spec =
    input.fleet?.find((a) => a.id === request.suggestedAircraftId) ??
    input.fleet?.find((a) => a.registration) ??
    input.fleet?.[0];
  const std = new Date(request.departureIso);
  const when = Number.isNaN(std.getTime()) ? now : std;
  const quotedCad = isRevenueCharter(request) ? desk.revenueCad : 0;
  const position = positioningNoteFor({ request, deskNote: desk.note });
  const feeTotal = Math.round((-(desk.landingCad + desk.handlingCad + desk.parkingCad)) * 100) / 100;
  const lines: QuoteMoneyLine[] = [
    {
      label: "Base charter",
      amountCad: desk.baseCharterCad ?? quotedCad,
      basis: "estimate",
    },
    ...(desk.repositionCad
      ? [{ label: "Empty-return / reposition (in offered fee)", amountCad: desk.repositionCad, basis: "estimate" as const }]
      : []),
    ...(desk.followOnCreditCad
      ? [{ label: "Follow-on credit (near dest)", amountCad: -desk.followOnCreditCad, basis: "estimate" as const }]
      : []),
    { label: "Quoted charter", amountCad: quotedCad, basis: "estimate" },
    { label: "Fuel", amountCad: -desk.fuelCad, basis: "estimate" },
    { label: "Landing / handling / parking", amountCad: feeTotal, basis: "estimate" },
  ];
  const netCad = Math.round((quotedCad - desk.fuelCad + feeTotal) * 100) / 100;
  const ref = quoteRefFor({
    registration: spec?.registration,
    originIcao: request.originIcao,
    destIcao: request.destIcao,
    when,
  });
  return {
    generatedAtUtc: now.toISOString(),
    filename: quoteFilename(ref),
    quoteRef: ref,
    operatorName: companyName,
    homeIcao,
    vendorName: QUOTE_VENDOR,
    currency,
    clientName: request.clientName.trim(),
    clientCompany: request.clientCompany.trim(),
    trip: {
      stdLabel: Number.isNaN(std.getTime()) ? "—" : formatHalifaxDateTime(std),
      originIcao: request.originIcao,
      destIcao: request.destIcao,
      originName: request.originName,
      destName: request.destName,
      aircraftName: spec?.name ?? "Fleet tail",
      registration: spec?.registration ?? "—",
      paxCount: request.paxCount,
      cargoLb: request.cargoLb,
      flightTypeLabel: FLIGHT_TYPE_LABEL[request.flightType],
    },
    quotedCad,
    lines,
    netCad,
    notes: uniqueNotes([
      desk.note,
      request.specialRequests ? `Special: ${request.specialRequests}` : null,
      position,
      "Estimate from the company rate card and catalogs. Classic desk math — not invented figures.",
    ]),
    positioningNote: position,
    estimate: true,
    logoImage: quoteLogoImage({ logoRef: input.logoRef, logoPlate: input.logoPlate }),
  };
}

function toWinAnsi(text: string): string {
  return text
    .replace(/[–—]/g, "-")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/€/g, "EUR ")
    .replace(/£/g, "GBP ")
    .replace(/−/g, "-")
    .replace(/·/g, " - ")
    .replace(/\u00a0/g, " ")
    .replace(/[^\x20-\x7E]/g, "?");
}

function escapePdf(text: string): string {
  return toWinAnsi(text).replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");
}

function approxWidth(text: string, size: number, bold?: boolean): number {
  return toWinAnsi(text).length * size * (bold ? 0.6 : 0.5);
}

function writeSimplePdf(runs: PdfRun[], rules: PdfRule[]): Uint8Array {
  const ops: string[] = ["0.25 G", "0.6 w"];
  for (const rule of rules) {
    const y1 = PAGE_H - rule.y1;
    const y2 = PAGE_H - rule.y2;
    ops.push(`${rule.x1.toFixed(1)} ${y1.toFixed(1)} m ${rule.x2.toFixed(1)} ${y2.toFixed(1)} l S`);
  }
  ops.push("0 g");
  for (const run of runs) {
    const y = PAGE_H - run.y;
    const font = run.bold ? "F2" : "F1";
    ops.push(`BT /${font} ${run.size} Tf ${run.x.toFixed(1)} ${y.toFixed(1)} Td (${escapePdf(run.text)}) Tj ET`);
  }
  const stream = ops.join("\n");
  const objects = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    "<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_W} ${PAGE_H}] /Contents 4 0 R /Resources << /Font << /F1 5 0 R /F2 6 0 R >> >> >>`,
    `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`,
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>",
  ];
  let body = "%PDF-1.4\n";
  const offsets = [0];
  for (let i = 0; i < objects.length; i += 1) {
    offsets.push(body.length);
    body += `${i + 1} 0 obj\n${objects[i]}\nendobj\n`;
  }
  const xrefAt = body.length;
  body += `xref\n0 ${objects.length + 1}\n`;
  body += "0000000000 65535 f \n";
  for (let i = 1; i <= objects.length; i += 1) {
    body += `${String(offsets[i]).padStart(10, "0")} 00000 n \n`;
  }
  body += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefAt}\n%%EOF\n`;
  return new TextEncoder().encode(body);
}

export function quotePdfPlainText(doc: CharterQuoteDoc): string {
  const client =
    doc.clientName || doc.clientCompany
      ? `Prepared for ${[doc.clientName, doc.clientCompany].filter(Boolean).join(" / ")}`
      : "";
  const trip = [
    `Dates ${doc.trip.stdLabel}`,
    `Origin ${doc.trip.originIcao} ${doc.trip.originName}`,
    `Destination ${doc.trip.destIcao} ${doc.trip.destName}`,
    `Aircraft ${doc.trip.aircraftName}`,
    `Tail ${doc.trip.registration}`,
    `Passengers ${doc.trip.paxCount}`,
    `Cargo ${doc.trip.cargoLb} lb`,
    `Type ${doc.trip.flightTypeLabel}`,
  ];
  const price = doc.lines.map(
    (line) => `${line.label} ${formatQuoteMoney(line.amountCad, doc.currency)} ${formatPnlBasis(line.basis)}`,
  );
  return [
    doc.operatorName,
    `Home base ${doc.homeIcao}`,
    "CHARTER QUOTATION",
    doc.quoteRef,
    formatQuoteMoney(doc.quotedCad, doc.currency),
    doc.estimate ? "Estimate" : "Estimate / Actual",
    client,
    "TRIP",
    ...trip,
    `PRICE home currency ${doc.currency}`,
    ...price,
    `Net ${formatQuoteMoney(doc.netCad, doc.currency)}`,
    "NOTES",
    doc.positioningNote ?? "",
    ...doc.notes.filter((note) => note !== doc.positioningNote),
    `Prepared with ${doc.vendorName}`,
    `${doc.operatorName} is the operator. ${SOFTWARE_VENDOR} is the software vendor.`,
    "Estimate only. Not a flight plan.",
  ]
    .filter((line) => line.trim())
    .join("\n");
}

function layoutQuote(doc: CharterQuoteDoc, hasMark = false): { runs: PdfRun[]; rules: PdfRule[] } {
  const runs: PdfRun[] = [];
  const rules: PdfRule[] = [];
  let y = 44;
  const add = (text: string, opts?: { x?: number; size?: number; bold?: boolean }) => {
    runs.push({ text, x: opts?.x ?? MARGIN, y, size: opts?.size ?? 9, bold: opts?.bold });
  };
  const pair = (label: string, value: string, valueBold = false) => {
    add(label, { size: 9 });
    add(value, { x: 188, size: 9, bold: valueBold });
    y += 13;
  };
  const moneyRow = (label: string, amount: number, basis: MoneyBasis) => {
    add(label, { size: 9 });
    const money = formatQuoteMoney(amount, doc.currency);
    add(money, { x: RIGHT - approxWidth(money, 9), size: 9 });
    add(formatPnlBasis(basis), { x: 360, size: 8 });
    y += 13;
  };

  add(doc.operatorName, { size: 16, bold: true, x: hasMark ? MARGIN + 36 : MARGIN });
  add(doc.quoteRef, { x: RIGHT - approxWidth(doc.quoteRef, 9), size: 9 });
  y += 16;
  add(`Home base ${doc.homeIcao}  ·  Charter operator`);
  add(doc.trip.stdLabel, { x: RIGHT - approxWidth(doc.trip.stdLabel, 9), size: 9 });
  y += 14;
  rules.push({ x1: MARGIN, y1: y, x2: RIGHT, y2: y });
  y += 16;
  add("CHARTER QUOTATION", { size: 12, bold: true });
  const hero = formatQuoteMoney(doc.quotedCad, doc.currency);
  add(hero, { x: RIGHT - approxWidth(hero, 12, true), size: 12, bold: true });
  y += 14;
  add(doc.estimate ? "Price is an estimate" : "Estimate vs Actual", { size: 8 });
  add(`Home ${doc.currency}`, { x: RIGHT - approxWidth(`Home ${doc.currency}`, 8), size: 8 });
  y += 16;
  if (doc.clientName || doc.clientCompany) {
    add(`Prepared for ${[doc.clientName, doc.clientCompany].filter(Boolean).join(" / ")}`);
    y += 16;
  }

  add("TRIP", { size: 10, bold: true });
  y += 14;
  pair("Dates", doc.trip.stdLabel);
  pair("Origin", `${doc.trip.originIcao}  ${doc.trip.originName}`);
  pair("Destination", `${doc.trip.destIcao}  ${doc.trip.destName}`);
  pair("Aircraft", doc.trip.aircraftName);
  pair("Tail", doc.trip.registration);
  pair("Passengers", String(doc.trip.paxCount));
  pair("Cargo", `${doc.trip.cargoLb} lb`);
  pair("Type", doc.trip.flightTypeLabel);
  y += 6;
  rules.push({ x1: MARGIN, y1: y, x2: RIGHT, y2: y });
  y += 16;

  add(`PRICE  ·  home currency ${doc.currency}`, { size: 10, bold: true });
  y += 14;
  for (const line of doc.lines) {
    moneyRow(line.label, line.amountCad, line.basis);
  }
  y += 2;
  rules.push({ x1: 188, y1: y, x2: RIGHT, y2: y });
  y += 14;
  add("Net (desk estimate)", { size: 9, bold: true });
  const net = formatQuoteMoney(doc.netCad, doc.currency);
  add(net, { x: RIGHT - approxWidth(net, 9, true), size: 9, bold: true });
  y += 20;

  add("NOTES", { size: 10, bold: true });
  y += 14;
  if (doc.positioningNote) {
    add(doc.positioningNote, { size: 9, bold: true });
    y += 13;
  }
  for (const note of doc.notes) {
    if (doc.positioningNote && note === doc.positioningNote) continue;
    add(note, { size: 8 });
    y += 12;
  }

  const footerY = PAGE_H - 56;
  rules.push({ x1: MARGIN, y1: footerY - 10, x2: RIGHT, y2: footerY - 10 });
  runs.push({
    text: `Prepared with ${doc.vendorName}`,
    x: MARGIN,
    y: footerY,
    size: 8,
    bold: true,
  });
  runs.push({
    text: `${doc.operatorName} is the operator. ${SOFTWARE_VENDOR} is the software vendor.`,
    x: MARGIN,
    y: footerY + 12,
    size: 8,
  });
  runs.push({
    text: "Estimate only. Not a flight plan.",
    x: MARGIN,
    y: footerY + 24,
    size: 8,
  });
  return { runs, rules };
}

export function renderQuotePdf(doc: CharterQuoteDoc): Uint8Array {
  const mark = doc.logoImage?.flate?.length ? doc.logoImage : null;
  const { runs, rules } = layoutQuote(doc, Boolean(mark));
  return mark ? writeQuotePdfWithMark(runs, rules, mark) : writeSimplePdf(runs, rules);
}

function writeQuotePdfWithMark(runs: PdfRun[], rules: PdfRule[], mark: AirlinePdfImage): Uint8Array {
  const encoder = new TextEncoder();
  const ascii = (s: string) => encoder.encode(s);
  const chunks: Uint8Array[] = [];
  let length = 0;
  const push = (bytes: Uint8Array) => {
    chunks.push(bytes);
    length += bytes.length;
  };
  const pushAscii = (s: string) => push(ascii(s));
  const offsets: number[] = [0];
  const writeObj = (body: Uint8Array) => {
    offsets.push(length);
    pushAscii(`${offsets.length - 1} 0 obj\n`);
    push(body);
    pushAscii("\nendobj\n");
  };
  const ops: string[] = ["0.25 G", "0.6 w"];
  for (const rule of rules) {
    const y1 = PAGE_H - rule.y1;
    const y2 = PAGE_H - rule.y2;
    ops.push(`${rule.x1.toFixed(1)} ${y1.toFixed(1)} m ${rule.x2.toFixed(1)} ${y2.toFixed(1)} l S`);
  }
  const drawW = 28;
  const drawH = 28;
  const imagePdfY = PAGE_H - 44 - drawH;
  ops.push("q");
  ops.push(`${drawW} 0 0 ${drawH} ${MARGIN} ${imagePdfY.toFixed(1)} cm`);
  ops.push("/Im1 Do");
  ops.push("Q");
  ops.push("0 g");
  for (const run of runs) {
    const y = PAGE_H - run.y;
    const font = run.bold ? "F2" : "F1";
    ops.push(`BT /${font} ${run.size} Tf ${run.x.toFixed(1)} ${y.toFixed(1)} Td (${escapePdf(run.text)}) Tj ET`);
  }
  const stream = ops.join("\n");
  pushAscii("%PDF-1.4\n");
  writeObj(ascii("<< /Type /Catalog /Pages 2 0 R >>"));
  writeObj(ascii("<< /Type /Pages /Kids [3 0 R] /Count 1 >>"));
  writeObj(
    ascii(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_W} ${PAGE_H}] /Contents 4 0 R /Resources << /Font << /F1 5 0 R /F2 6 0 R >> /XObject << /Im1 7 0 R >> >> >>`,
    ),
  );
  writeObj(ascii(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`));
  writeObj(ascii("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>"));
  writeObj(ascii("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>"));
  const header = ascii(
    `<< /Type /XObject /Subtype /Image /Width ${mark.width} /Height ${mark.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /FlateDecode /Length ${mark.flate.length} >>\nstream\n`,
  );
  const footer = ascii("\nendstream");
  const imageBody = new Uint8Array(header.length + mark.flate.length + footer.length);
  imageBody.set(header, 0);
  imageBody.set(mark.flate, header.length);
  imageBody.set(footer, header.length + mark.flate.length);
  writeObj(imageBody);
  const xrefAt = length;
  pushAscii(`xref\n0 ${offsets.length}\n`);
  pushAscii("0000000000 65535 f \n");
  for (let i = 1; i < offsets.length; i += 1) {
    pushAscii(`${String(offsets[i]).padStart(10, "0")} 00000 n \n`);
  }
  pushAscii(`trailer\n<< /Size ${offsets.length} /Root 1 0 R >>\nstartxref\n${xrefAt}\n%%EOF\n`);
  const out = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

export function pdfBytesToDataUrl(bytes: Uint8Array): string {
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]!);
  const b64 = typeof btoa === "function" ? btoa(bin) : Buffer.from(bytes).toString("base64");
  return `data:application/pdf;base64,${b64}`;
}

export function makeQuoteAttachment(doc: CharterQuoteDoc): MissionQuoteAttachment {
  return {
    generatedAtUtc: doc.generatedAtUtc,
    filename: doc.filename,
    quoteRef: doc.quoteRef,
    currency: doc.currency,
    revenueCad: doc.quotedCad,
    note: doc.positioningNote || doc.notes[0] || "Desk rate card",
    dataUrl: pdfBytesToDataUrl(renderQuotePdf(doc)),
  };
}

export function quoteAttachmentForDesk(input: BuildQuoteDocInput): MissionQuoteAttachment | null {
  const doc = buildCharterQuoteDoc(input);
  return doc ? makeQuoteAttachment(doc) : null;
}

export function hydrateQuoteAttachment(raw: unknown): MissionQuoteAttachment | undefined {
  if (!isRecord(raw)) return undefined;
  if (typeof raw.dataUrl !== "string" || !raw.dataUrl.startsWith("data:application/pdf")) return undefined;
  if (typeof raw.filename !== "string" || !raw.filename.trim()) return undefined;
  const currency = hydrateCurrency(raw.currency);
  const revenueCad = typeof raw.revenueCad === "number" && Number.isFinite(raw.revenueCad) ? raw.revenueCad : 0;
  return {
    generatedAtUtc: typeof raw.generatedAtUtc === "string" ? raw.generatedAtUtc : new Date().toISOString(),
    filename: raw.filename.trim(),
    quoteRef: typeof raw.quoteRef === "string" && raw.quoteRef.trim() ? raw.quoteRef.trim() : raw.filename.trim(),
    currency,
    revenueCad,
    note: typeof raw.note === "string" ? raw.note : "",
    dataUrl: raw.dataUrl,
  };
}

export function pdfContainsAscii(bytes: Uint8Array, needle: string): boolean {
  const hay = new TextDecoder("latin1").decode(bytes);
  return hay.includes(toWinAnsi(needle));
}
